import type { User } from '../../types';
import type { ChangeFormField, ChangeRequest, ChangeSubtask } from '../../types/changes';
import { isChangeManager } from './changePermissions';
import { SUBTASK_OPERATE_STATUSES } from './changeLabels';

export function checklistRespondentRole(field: Pick<ChangeFormField, 'respondent_role'>): 'requester' | 'assignee' {
  const role = (field.respondent_role || 'assignee').trim().toLowerCase();
  return role === 'requester' ? 'requester' : 'assignee';
}

export function userIsSubtaskRequesterStakeholder(
  change: ChangeRequest,
  subtask: ChangeSubtask,
  user?: User | null,
): boolean {
  const userId = user?.id;
  if (!userId) return false;
  if (isChangeManager(user)) return true;
  const candidates = [change.requester_id, change.change_owner_id, subtask.created_by];
  return candidates.some((id) => id != null && Number(id) === Number(userId));
}

export function canEditRequesterChecklistFields(
  change: ChangeRequest,
  subtask: ChangeSubtask,
  user?: User | null,
): boolean {
  if (!SUBTASK_OPERATE_STATUSES.has(change.status)) return false;
  if (subtask.status === 'concluida' || subtask.invalidated_at) return false;
  return userIsSubtaskRequesterStakeholder(change, subtask, user);
}

/** Um único formulário quando o usuário pode editar perguntas de solicitante e do grupo (evita dois “Salvar respostas”). */
export function shouldUseUnifiedChecklistEditor(
  change: ChangeRequest,
  subtask: ChangeSubtask,
  user: User | null | undefined,
  canEditAssignee: boolean,
  requester: ChangeFormField[],
  assignee: ChangeFormField[],
): boolean {
  if (!requester.length || !assignee.length) return false;
  if (!canEditAssignee) return false;
  return canEditRequesterChecklistFields(change, subtask, user);
}

export function splitChecklistByRespondent(fields: ChangeFormField[]): {
  requester: ChangeFormField[];
  assignee: ChangeFormField[];
} {
  const requester: ChangeFormField[] = [];
  const assignee: ChangeFormField[] = [];
  fields.forEach((field) => {
    if (checklistRespondentRole(field) === 'requester') {
      requester.push(field);
    } else {
      assignee.push(field);
    }
  });
  return { requester, assignee };
}
